import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Search,
  MapPin,
  Briefcase,
  Star,
  DollarSign,
  Users,
  TrendingUp,
  Megaphone,
  Clock,
  Zap,
  Layers,
  Sparkles,
  XCircle,
  Plus,
  CreditCard,
  FlaskConical,
  BadgeDollarSign,
  Settings2,
  ChevronUp,
  ChevronDown,
  Gift,
  UserPlus,
  Send,
  X,
  Mail,
  Link2,
  Copy,
  Share2,
  Check,
  Loader2,
  CheckCircle2,
  Trophy
} from 'lucide-react';
import { resolveImageUrl } from '../lib/api';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { ActivateModal } from '../components/PremiumGate';

// Own Slot Manager — pricing adjustment + promotional icons
const OwnSlotManager = ({ listing }) => {
  const [slotPrices, setSlotPrices] = useState({});
  const [editingSlot, setEditingSlot] = useState(null);
  const [editPrice, setEditPrice] = useState(20);
  const [saving, setSaving] = useState(false);
  const [enablingBidding, setEnablingBidding] = useState(false);
  const [biddingEnabled, setBiddingEnabled] = useState(listing.bidding_enabled || false);

  useEffect(() => {
    const prices = {};
    listing.slots.forEach(s => { prices[s.position] = s.price_per_month || 20; });
    setSlotPrices(prices);
  }, [listing.slots]);

  const handleSavePrice = async (position) => {
    setSaving(true);
    try {
      const resp = await api.updateSlotPricing(position, editPrice);
      setSlotPrices(prev => ({ ...prev, [position]: editPrice }));
      toast.success(resp.message);
      setEditingSlot(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update pricing');
    }
    setSaving(false);
  };

  const handleEnableBidding = async () => {
    setEnablingBidding(true);
    try {
      await api.enableBidding();
      setBiddingEnabled(true);
      toast.success('Bidding is now enabled! Pros can bid premium prices for your slots.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to enable bidding');
    }
    setEnablingBidding(false);
  };

  return (
    <div className="space-y-3" data-testid="own-slot-manager">
      {/* Filled Slots - Show who is advertising */}
      {listing.filled_slots && listing.filled_slots.length > 0 && (
        <>
          <p className="text-xs text-muted-foreground font-medium">Currently advertising on your signature:</p>
          <div className="space-y-2">
            {listing.filled_slots.map((fs) => (
              <div key={fs.position} className="flex items-center gap-2 p-2.5 rounded-lg border border-blue-200 bg-blue-50/50" data-testid={`own-filled-slot-${fs.position}`}>
                {fs.advertiser.profile_photo ? (
                  <div className="w-7 h-7 rounded-full overflow-hidden border border-blue-300 flex-shrink-0">
                    <img src={fs.advertiser.profile_photo} alt="" className="w-full h-full object-cover" />
                  </div>
                ) : (
                  <div className="w-7 h-7 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-[10px] font-bold">{fs.advertiser.initial}</span>
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-blue-800">Slot #{fs.position}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-200 text-blue-700 font-medium">Booked</span>
                  </div>
                  <p className="text-xs text-blue-700 truncate">{fs.advertiser.name} · {fs.advertiser.profession}{fs.advertiser.company ? ` · ${fs.advertiser.company}` : ''}</p>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Available Slots */}
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground font-medium">
          {listing.slots.length > 0 ? 'Available slots — set pricing to control earnings:' : 'All slots are booked!'}
        </p>
      </div>
      <div className="space-y-2">
        {listing.slots.map((slot) => {
          const price = slotPrices[slot.position] || 20;
          const isEditing = editingSlot === slot.position;
          const earnings = price * 0.5;
          return (
            <div key={slot.slot_id} className="flex items-center gap-2 p-2.5 rounded-lg border border-green-200 bg-green-50/50" data-testid={`own-slot-${slot.position}`}>
              <div className="w-7 h-7 rounded-md bg-green-500 flex items-center justify-center flex-shrink-0">
                <BadgeDollarSign className="w-4 h-4 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-green-800">Slot #{slot.position}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-200 text-green-700 font-medium">Available</span>
                </div>
                {!isEditing ? (
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-green-700 font-medium">${price}/mo</span>
                    <span className="text-[10px] text-muted-foreground">·</span>
                    <span className="text-xs text-green-600">You earn ${earnings}/mo</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 mt-1">
                    <div className="flex items-center border border-green-300 rounded-md bg-white">
                      <span className="text-xs font-medium text-green-700 pl-2">$</span>
                      <input
                        type="number"
                        value={editPrice}
                        onChange={(e) => setEditPrice(Math.max(10, Math.min(500, Number(e.target.value))))}
                        className="w-16 text-xs py-1 px-1 bg-transparent focus:outline-none"
                        min={10} max={500}
                        data-testid={`price-input-${slot.position}`}
                      />
                      <span className="text-[10px] text-muted-foreground pr-2">/mo</span>
                    </div>
                    <span className="text-[10px] text-green-600">Earn ${(editPrice * 0.5).toFixed(0)}/mo</span>
                    <Button size="sm" className="h-6 px-2 text-[10px]" onClick={() => handleSavePrice(slot.position)} disabled={saving} data-testid={`save-price-${slot.position}`}>
                      {saving ? '...' : 'Save'}
                    </Button>
                    <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[10px]" onClick={() => setEditingSlot(null)}>
                      Cancel
                    </Button>
                  </div>
                )}
              </div>
              {!isEditing && (
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-green-600 hover:bg-green-100" onClick={() => { setEditingSlot(slot.position); setEditPrice(price); }} data-testid={`edit-price-${slot.position}`}>
                  <Settings2 className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
          );
        })}
      </div>
      {listing.slots.length < 5 && (
        <Button
          size="sm"
          variant="outline"
          onClick={() => {}}
          className="text-xs h-8 w-full border-dashed border-green-500/50 text-green-600 hover:bg-green-50"
          data-testid={`add-slot-own-${listing.id}`}
        >
          <Plus className="w-3 h-3 mr-1" />
          Add Slot (+$20/mo)
        </Button>
      )}
      {!biddingEnabled ? (
        <Button
          size="sm"
          variant="outline"
          onClick={handleEnableBidding}
          disabled={enablingBidding}
          className="text-xs h-8 w-full border-amber-300 text-amber-700 hover:bg-amber-50 mt-1"
          data-testid="enable-bidding-btn"
        >
          <TrendingUp className="w-3 h-3 mr-1" />
          {enablingBidding ? 'Enabling...' : 'Enable Bidding (let pros bid higher prices)'}
        </Button>
      ) : (
        <div className="flex items-center gap-1.5 text-[10px] text-amber-600 bg-amber-50 px-2.5 py-1.5 rounded-md mt-1" data-testid="bidding-enabled-badge">
          <TrendingUp className="w-3 h-3" />
          <span className="font-medium">Bidding enabled — pros can bid premium prices for your slots</span>
        </div>
      )}
    </div>
  );
};

// ==================== MARKETPLACE INVITE PRO MODAL ====================
const MarketplaceInviteModal = ({ onClose }) => {
  const [referralData, setReferralData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailInput, setEmailInput] = useState('');
  const [nameInput, setNameInput] = useState('');
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);
  const [sentList, setSentList] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const data = await api.getReferralLink();
        setReferralData(data);
      } catch { /* ignore */ }
      setLoading(false);
    })();
  }, []);

  const handleCopyLink = () => {
    navigator.clipboard?.writeText(referralData?.referral_link || '');
    setCopied(true);
    toast.success('Referral link copied!');
    setTimeout(() => setCopied(false), 2500);
  };

  const handleSendInvite = async () => {
    if (!emailInput.trim() || !emailInput.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }
    setSending(true);
    try {
      await api.importContacts([{ email: emailInput.trim(), name: nameInput.trim() }]);
      setSentList(prev => [...prev, emailInput.trim()]);
      toast.success(`Invitation sent to ${emailInput.trim()}`);
      setEmailInput('');
      setNameInput('');
      const data = await api.getReferralLink();
      setReferralData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send invitation');
    }
    setSending(false);
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: 'Join ConnectMail',
        text: 'Advertise on my email signature! Join ConnectMail — the business networking platform for professionals!',
        url: referralData?.referral_link || window.location.origin,
      });
    } else { handleCopyLink(); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="marketplace-invite-modal">
        <div className="bg-gradient-to-r from-emerald-600 to-green-600 p-5 rounded-t-2xl relative">
          <button onClick={onClose} className="absolute top-3 right-3 p-1.5 hover:bg-white/20 rounded-full transition-colors">
            <X className="w-5 h-5 text-white" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Gift className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white font-['Outfit']">Invite a Pro</h2>
              <p className="text-emerald-100 text-sm">Grow your network &amp; help others find you</p>
            </div>
          </div>
        </div>
        <div className="p-5 space-y-5">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-3">
                <div className="text-center p-3 bg-emerald-50 rounded-xl">
                  <p className="text-2xl font-bold text-emerald-700">{referralData?.total_invited || 0}</p>
                  <p className="text-[10px] text-emerald-600 font-medium">Invited</p>
                </div>
                <div className="text-center p-3 bg-blue-50 rounded-xl">
                  <p className="text-2xl font-bold text-blue-700">{referralData?.total_referrals || 0}</p>
                  <p className="text-[10px] text-blue-600 font-medium">Joined</p>
                </div>
                <div className="text-center p-3 bg-amber-50 rounded-xl">
                  <p className="text-2xl font-bold text-amber-700">{referralData?.active_free_months || 0}</p>
                  <p className="text-[10px] text-amber-600 font-medium">Free Months</p>
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-700 mb-1.5 block">Your Referral Link</label>
                <div className="flex gap-2">
                  <div className="flex-1 flex items-center bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-600 truncate">
                    <Link2 className="w-4 h-4 mr-2 flex-shrink-0 text-gray-400" />
                    <span className="truncate">{referralData?.referral_link || '...'}</span>
                  </div>
                  <Button size="sm" variant={copied ? 'default' : 'outline'} className={`h-10 px-3 ${copied ? 'bg-emerald-600 text-white' : ''}`}
                    onClick={handleCopyLink} data-testid="mp-copy-referral-link">
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                  <Button size="sm" className="h-10 px-3 bg-emerald-600 hover:bg-emerald-700" onClick={handleShare}>
                    <Share2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="border-t border-gray-100 pt-4">
                <label className="text-xs font-semibold text-gray-700 mb-2 block flex items-center gap-1.5">
                  <Mail className="w-3.5 h-3.5" /> Send an Email Invitation
                </label>
                <div className="space-y-2">
                  <Input placeholder="Name (optional)" value={nameInput} onChange={e => setNameInput(e.target.value)}
                    className="h-9 text-sm" data-testid="mp-invite-name" />
                  <div className="flex gap-2">
                    <Input placeholder="Email address" type="email" value={emailInput} onChange={e => setEmailInput(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleSendInvite()} className="flex-1 h-9 text-sm" data-testid="mp-invite-email" />
                    <Button size="sm" className="h-9 bg-emerald-600 hover:bg-emerald-700" onClick={handleSendInvite} disabled={sending}
                      data-testid="mp-send-invite-btn">
                      {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-3.5 h-3.5 mr-1" />Send</>}
                    </Button>
                  </div>
                </div>
              </div>

              {sentList.length > 0 && (
                <div className="bg-emerald-50 rounded-lg p-3">
                  <p className="text-xs font-semibold text-emerald-800 mb-1.5">Just Invited</p>
                  {sentList.map((email, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-emerald-700">
                      <CheckCircle2 className="w-3.5 h-3.5" /><span>{email}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export const AdMarketplace = ({ embedded = false, onNavigateToApprovals }) => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const highlightId = searchParams.get('highlight');
  const [listings, setListings] = useState([]);
  const [myAds, setMyAds] = useState([]);
  const [openSlots, setOpenSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedListing, setSelectedListing] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [adMessage, setAdMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [adDialogOpen, setAdDialogOpen] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [activeView, setActiveView] = useState('marketplace');
  const [recommended, setRecommended] = useState([]);
  const [recommendedLoading, setRecommendedLoading] = useState(false);
  const [addSlotDialogOpen, setAddSlotDialogOpen] = useState(false);
  const [addSlotListing, setAddSlotListing] = useState(null);
  const [purchasingSlot, setPurchasingSlot] = useState(false);
  const [bidDialogOpen, setBidDialogOpen] = useState(false);
  const [bidTarget, setBidTarget] = useState(null);
  const [bidAmount, setBidAmount] = useState(25);
  const [bidMessage, setBidMessage] = useState('');
  const [submittingBid, setSubmittingBid] = useState(false);
  const [joiningWaitlist, setJoiningWaitlist] = useState(null);
  const [showInvitePopup, setShowInvitePopup] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showSlotActivate, setShowSlotActivate] = useState(false);
  const [foundingExtraSlot, setFoundingExtraSlot] = useState({ open: false, inUse: 0, freeQuota: 1, amount: 20, message: '' });
  const [unlockingExtraSlot, setUnlockingExtraSlot] = useState(false);
  const isPremium = user?.is_premium === true;

  const handleUnlockExtraSlot = async () => {
    setUnlockingExtraSlot(true);
    try {
      const data = await api.createCheckout('founding_extra_slot');
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error('Could not start checkout. Please try again.');
        setUnlockingExtraSlot(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start checkout.');
      setUnlockingExtraSlot(false);
    }
  };

  // Marketplace invite popup is no longer auto-shown — founding members already
  // get their first free ad slot at signup, so the old "invite 2 pros for a
  // free slot" popup would be misleading. Users can still open the Invite Pro
  // modal manually via the "Invite a Pro" tab.
  useEffect(() => {
    setShowInvitePopup(false);
  }, []);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [marketplaceData, adsData, openSlotsData] = await Promise.all([
        api.getMarketplace(),
        api.getMyBids(),
        api.getOpenSlots().catch(() => ({ open_slots: [] }))
      ]);
      let fetched = marketplaceData.listings || [];
      // If a highlighted pro is specified, sort them to the top
      if (highlightId) {
        fetched = [
          ...fetched.filter(l => l.user_id === highlightId),
          ...fetched.filter(l => l.user_id !== highlightId),
        ];
      }
      setListings(fetched);
      setMyAds(adsData.bids || []);
      setOpenSlots(openSlotsData.open_slots || []);
    } catch (error) {
      console.error('Failed to fetch marketplace:', error);
      toast.error('Failed to load marketplace');
    } finally {
      setLoading(false);
    }
  };

  const handlePlaceAd = async () => {
    if (!selectedListing || !selectedSlot) return;
    if (selectedListing.owner.is_sample) {
      toast.error('This is a sample account for demonstration only.');
      setAdDialogOpen(false);
      return;
    }
    setSubmitting(true);
    try {
      await api.placeBid(selectedSlot.slot_id, 20, adMessage);
      toast.success('Ad request submitted! Waiting for approval.');
      setAdDialogOpen(false);
      setSelectedListing(null);
      setSelectedSlot(null);
      setAdMessage('');
      fetchData();
    } catch (error) {
      console.error('Failed:', error);
      if (error.response?.status === 402) {
        const detail = error.response?.data?.detail;
        // Detail is an object when the server returns a structured 402 (e.g.,
        // founding members hitting their 1-free-slot limit). Otherwise it's
        // a plain "you need a subscription" message string.
        if (detail && typeof detail === 'object' && detail.code === 'founding_extra_slot_required') {
          setAdDialogOpen(false);
          setFoundingExtraSlot({
            open: true,
            inUse: detail.in_use,
            freeQuota: detail.free_quota,
            amount: detail.amount,
            message: detail.message,
          });
        } else {
          const msg = typeof detail === 'string' ? detail : 'You need an active subscription to place ads.';
          toast.error(msg + ' Redirecting to checkout…');
          setAdDialogOpen(false);
          try {
            const data = await api.createCheckout('subscription_1');
            if (data.url) {
              window.location.href = data.url;
            }
          } catch (checkoutErr) {
            toast.error('Could not start checkout. Please try again.');
          }
        }
      } else {
        toast.error(error.response?.data?.detail || 'Failed to place ad');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const openAdDialog = (listing, slot) => {
    if (listing.owner.is_sample) {
      toast.error('This is a sample/demo account. You cannot place ads here.');
      return;
    }
    setSelectedListing(listing);
    setSelectedSlot(slot);
    setAdDialogOpen(true);
  };

  const openAddSlotDialog = (listing) => {
    setAddSlotListing(listing);
    setAddSlotDialogOpen(true);
  };

  const handlePurchaseAdSlot = async () => {
    setPurchasingSlot(true);
    try {
      const data = await api.createCheckout('ad_slot');
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start checkout. Make sure your account is active.');
    } finally {
      setPurchasingSlot(false);
    }
  };

  const handleSearchPros = async () => {
    if (!searchTerm.trim()) {
      setSearchResults(null);
      setActiveView('marketplace');
      return;
    }
    setSearching(true);
    setActiveView('search');
    try {
      const data = await api.searchProfessionals({ q: searchTerm });
      setSearchResults(data.professionals || []);
    } catch {
      toast.error('Search failed');
    } finally {
      setSearching(false);
    }
  };

  const fetchRecommended = async () => {
    setRecommendedLoading(true);
    try {
      const data = await api.getRecommendedMatches();
      setRecommended(data.recommended || []);
    } catch {
      console.error('Failed to fetch recommended matches');
    } finally {
      setRecommendedLoading(false);
    }
  };

  const handleJoinWaitlist = async (targetUserId) => {
    setJoiningWaitlist(targetUserId);
    try {
      const resp = await api.joinWaitlist(targetUserId, 'Interested in advertising on your signature');
      toast.success(resp.message);
      fetchListings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to join waitlist');
    } finally {
      setJoiningWaitlist(null);
    }
  };

  const handlePlaceBid = async () => {
    if (!bidTarget) return;
    setSubmittingBid(true);
    try {
      const resp = await api.placePremiumBid(bidTarget.user_id, bidAmount, bidMessage);
      toast.success(resp.message);
      setBidDialogOpen(false);
      setBidTarget(null);
      setBidAmount(25);
      setBidMessage('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to place bid');
    } finally {
      setSubmittingBid(false);
    }
  };

  const openBidDialog = (listing) => {
    setBidTarget(listing);
    setBidAmount(25);
    setBidMessage('');
    setBidDialogOpen(true);
  };

  const filteredListings = listings.filter(listing => {
    const search = searchTerm.toLowerCase();
    return (
      listing.owner.name.toLowerCase().includes(search) ||
      listing.owner.profession.toLowerCase().includes(search) ||
      listing.owner.city?.toLowerCase().includes(search) ||
      listing.owner.state?.toLowerCase().includes(search)
    );
  });

  const hasRequestedSlot = (slotId) => {
    return myAds.some(ad =>
      `${ad.signature_id}-${ad.position}` === slotId && ad.status === 'pending'
    );
  };

  // Count total available slots across all listings
  const totalAvailableSlots = listings.reduce((sum, listing) => sum + (listing.available_slots || 0), 0);

  if (loading) {
    return embedded ? (
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-muted rounded w-1/4" />
          <div className="h-12 bg-muted rounded w-full max-w-md" />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="h-48 bg-muted rounded-lg" />
            ))}
          </div>
        </div>
    ) : (
      <Layout>
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-muted rounded w-1/4" />
          <div className="h-12 bg-muted rounded w-full max-w-md" />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="h-48 bg-muted rounded-lg" />
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  const content = (
      <div className="space-y-4 sm:space-y-6" data-testid="ad-marketplace">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-['Outfit'] tracking-tight">Ad Marketplace</h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-1">
            Place your ad on other professionals' email signatures — $20/month flat rate
          </p>
        </div>

        {/* View Toggle + Search */}
        <div className="flex flex-col gap-4">
          <div className="flex gap-2 flex-wrap">
            <Button
              variant={activeView === 'marketplace' ? 'default' : 'outline'}
              size="sm"
              onClick={() => { setActiveView('marketplace'); setSearchResults(null); setSearchTerm(''); }}
              data-testid="marketplace-view-btn"
            >
              Available Slots
            </Button>
            <Button
              variant={activeView === 'opening' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveView('opening')}
              data-testid="opening-soon-view-btn"
            >
              <Clock className="w-3.5 h-3.5 mr-1.5" />
              Opening Soon {openSlots.length > 0 && `(${openSlots.length})`}
            </Button>
            <Button
              variant={activeView === 'search' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveView('search')}
              data-testid="find-pro-view-btn"
            >
              <Search className="w-3.5 h-3.5 mr-1.5" />
              Find a Pro
            </Button>
            <Button
              variant={activeView === 'recommended' ? 'default' : 'outline'}
              size="sm"
              onClick={() => { setActiveView('recommended'); if (recommended.length === 0) fetchRecommended(); }}
              data-testid="recommended-view-btn"
            >
              <Sparkles className="w-3.5 h-3.5 mr-1.5" />
              Recommended
            </Button>
            <Button
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={() => setShowInviteModal(true)}
              data-testid="marketplace-invite-pro-btn"
            >
              <UserPlus className="w-3.5 h-3.5 mr-1.5" />
              Invite a Pro
            </Button>
          </div>
          <div className="flex gap-3">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder={activeView === 'search' ? "Search by name, company, or email..." : "Filter slots by name, profession, or location..."}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && activeView === 'search' && handleSearchPros()}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            {activeView === 'search' && (
              <Button onClick={handleSearchPros} disabled={searching} data-testid="search-pros-btn">
                {searching ? 'Searching...' : 'Search'}
              </Button>
            )}
          </div>
        </div>

        {/* Stats Banner */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
          <Card className="border-border/50">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-accent" />
              </div>
              <div>
                <p className="text-2xl font-bold">{listings.length}</p>
                <p className="text-xs text-muted-foreground">Professionals</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border/50">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Layers className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{totalAvailableSlots}</p>
                <p className="text-xs text-muted-foreground">Available Slots</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border/50 cursor-pointer hover:border-blue-300 hover:shadow-md transition-all" onClick={onNavigateToApprovals} data-testid="pending-requests-card">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Megaphone className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{myAds.filter(a => a.status === 'pending').length}</p>
                <p className="text-xs text-muted-foreground">Pending Requests</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border/50">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">$20</p>
                <p className="text-xs text-muted-foreground">Flat Rate / mo</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border/50">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">50%</p>
                <p className="text-xs text-muted-foreground">Owner Earns</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search Results View */}
        {activeView === 'search' && searchResults !== null && (
          <div>
            <h2 className="text-lg font-semibold mb-4">
              {searchResults.length > 0 ? `Found ${searchResults.length} professional${searchResults.length !== 1 ? 's' : ''}` : 'No professionals found'}
            </h2>
            {searchResults.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {searchResults.map((pro) => (
                  <Card key={pro.id} className="border-border/50" data-testid={`pro-${pro.id}`}>
                    <CardContent className="p-5">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 bg-accent rounded-lg flex items-center justify-center">
                          <span className="text-white font-bold text-lg">{pro.full_name?.charAt(0)}</span>
                        </div>
                        <div>
                          <Link to={`/card/${pro.id}`} className="font-semibold hover:text-primary transition-colors" data-testid={`search-pro-name-${pro.id}`}>{pro.full_name}</Link>
                          <p className="text-sm text-accent">{pro.profession_id?.replace(/_/g, ' ')}</p>
                        </div>
                      </div>
                      {pro.company && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                          <Briefcase className="w-4 h-4" />{pro.company}
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                        <MapPin className="w-4 h-4" />{pro.city}, {pro.state}
                      </div>
                      <div className="flex items-center justify-between pt-3 border-t border-border/50">
                        <span className="text-sm">
                          {pro.slot_count > 0 ? (
                            <Badge variant="default">{pro.slot_count} slot{pro.slot_count !== 1 ? 's' : ''} available</Badge>
                          ) : (
                            <Badge variant="secondary">No open slots</Badge>
                          )}
                        </span>
                        <span className="text-sm font-medium text-green-600">$20/mo</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="border-border/50 border-dashed">
                <CardContent className="p-8 text-center">
                  <Search className="w-10 h-10 text-muted-foreground/40 mx-auto mb-3" />
                  <p className="font-medium">No professionals found</p>
                  <p className="text-sm text-muted-foreground mt-1">Try a different search term</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Opening Soon Slots View */}
        {activeView === 'opening' && (
          <div data-testid="opening-soon-section">
            <h2 className="text-lg font-semibold mb-4">
              Opening Soon — Slots from Cancelled Subscriptions
            </h2>
            {openSlots.length === 0 ? (
              <Card className="border-border/50 border-dashed">
                <CardContent className="p-8 text-center">
                  <div className="w-12 h-12 bg-muted rounded-lg mx-auto mb-4 flex items-center justify-center">
                    <Clock className="w-6 h-6 text-muted-foreground" />
                  </div>
                  <h3 className="font-medium mb-2">No slots opening soon</h3>
                  <p className="text-sm text-muted-foreground">
                    Check back later — slots become available when advertisers cancel.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {openSlots.map((slot) => (
                  <Card key={slot.slot_id} className="border-border/50 border-l-4 border-l-amber-400" data-testid={`open-slot-${slot.slot_id}`}>
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                            <Zap className="w-6 h-6 text-amber-600" />
                          </div>
                          <div>
                            <Link to={`/card/${slot.owner.id}`} className="font-semibold hover:text-primary transition-colors" data-testid={`slot-owner-name-${slot.slot_id}`}>{slot.owner.name}</Link>
                            <p className="text-sm text-accent">{slot.owner.profession}</p>
                          </div>
                        </div>
                        <Badge variant="outline" className="text-xs border-amber-400 text-amber-600">
                          Opening Soon
                        </Badge>
                      </div>
                      {slot.owner.company && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                          <Briefcase className="w-4 h-4" />{slot.owner.company}
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                        <MapPin className="w-4 h-4" />{slot.owner.city}, {slot.owner.state}
                      </div>
                      {slot.opening_date && (
                        <p className="text-xs text-amber-600 font-medium mb-3">
                          Available: {new Date(slot.opening_date).toLocaleDateString()}
                        </p>
                      )}
                      <div className="flex items-center justify-between pt-3 border-t border-border/50">
                        <Badge variant="secondary" className="text-xs">Slot #{slot.position}</Badge>
                        <span className="text-sm font-semibold text-green-600">$20/mo</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Recommended Matches View */}
        {activeView === 'recommended' && (
          <div data-testid="recommended-section">
            <h2 className="text-lg font-semibold mb-4">
              <Sparkles className="w-4 h-4 inline mr-2 text-amber-500" />
              Recommended Matches for You
            </h2>
            {recommendedLoading ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3].map(i => <div key={i} className="h-48 bg-muted rounded-lg animate-pulse" />)}
              </div>
            ) : recommended.length === 0 ? (
              <Card className="border-border/50 border-dashed">
                <CardContent className="p-8 text-center">
                  <Sparkles className="w-10 h-10 text-muted-foreground/40 mx-auto mb-3" />
                  <h3 className="font-medium mb-2">No recommended matches yet</h3>
                  <p className="text-sm text-muted-foreground">
                    We'll suggest complementary professionals as more users join the marketplace.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {recommended.map((match) => (
                  <Card
                    key={match.id}
                    className="border-border/50 border-l-4 border-l-amber-400 hover-lift"
                    data-testid={`recommended-${match.id}`}
                  >
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          {match.owner.profile_photo ? (
                            <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-amber-300">
                              <img
                                src={resolveImageUrl(match.owner.profile_photo)}
                                alt={match.owner.name}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          ) : (
                            <div className="w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center">
                              <span className="text-white font-bold text-lg">
                                {match.owner.name.charAt(0)}
                              </span>
                            </div>
                          )}
                          <div>
                            <p className="font-semibold">{match.owner.name}</p>
                            <p className="text-sm text-amber-600">{match.owner.profession}</p>
                          </div>
                        </div>
                        <Badge variant="outline" className="text-xs border-amber-400 text-amber-600">
                          <Sparkles className="w-3 h-3 mr-1" />
                          Match
                        </Badge>
                      </div>

                      {match.owner.company && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                          <Briefcase className="w-4 h-4" />
                          {match.owner.company}
                        </div>
                      )}

                      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                        <MapPin className="w-4 h-4" />
                        {match.owner.city}, {match.owner.state}
                      </div>

                      <p className="text-xs text-amber-700 bg-amber-50 px-3 py-1.5 rounded-md mb-4">
                        {match.match_reason}
                      </p>

                      <div className="flex items-center justify-between pt-3 border-t border-border/50">
                        <div className="flex items-center gap-2">
                          <Star className="w-4 h-4 text-yellow-500" />
                          <span className="text-sm font-medium">
                            {match.owner.reputation_score?.toFixed(1) || '0.0'} rep
                          </span>
                        </div>
                        <Badge variant="default" className="bg-accent text-white text-xs">
                          {match.available_slots} slot{match.available_slots !== 1 ? 's' : ''}
                        </Badge>
                      </div>

                      <div className="mt-4 space-y-2">
                        <p className="text-xs text-muted-foreground font-medium">Place your ad:</p>
                        <div className="flex flex-wrap gap-2">
                          {match.slots.map((slot) => {
                            const alreadyRequested = hasRequestedSlot(slot.slot_id);
                            return (
                              <Button
                                key={slot.slot_id}
                                size="sm"
                                variant={alreadyRequested ? "secondary" : "outline"}
                                disabled={alreadyRequested}
                                onClick={() => openAdDialog(match, slot)}
                                className="text-xs h-8"
                                data-testid={`rec-slot-btn-${slot.slot_id}`}
                              >
                                #{slot.position} {alreadyRequested && '(Pending)'}
                              </Button>
                            );
                          })}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Marketplace Listings - Consolidated by User */}
        {activeView === 'marketplace' && (
        filteredListings.length === 0 ? (
          <Card className="border-border/50 border-dashed">
            <CardContent className="p-8 text-center">
              <div className="w-12 h-12 bg-muted rounded-lg mx-auto mb-4 flex items-center justify-center">
                <Users className="w-6 h-6 text-muted-foreground" />
              </div>
              <h3 className="font-medium mb-2">No professionals available</h3>
              <p className="text-sm text-muted-foreground">
                {searchTerm ? 'Try adjusting your search' : 'No ad slots available at the moment'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredListings.map((listing) => {
              const isHighlighted = highlightId && listing.user_id === highlightId;
              return (
              <Card
                key={listing.id}
                className={`border-border/50 professional-card hover-lift ${isHighlighted ? 'ring-2 ring-primary/50 border-primary/30 shadow-lg' : ''} ${listing.is_own ? 'ring-2 ring-green-400/40 border-green-400/30' : ''}`}
                data-testid={`listing-${listing.id}`}
              >
                <CardContent className="p-5">
                  {listing.is_own && (
                    <div className="flex items-center gap-1.5 mb-3">
                      <Badge className="bg-green-100 text-green-700 text-[10px] font-semibold border-green-200" data-testid="own-listing-badge">
                        Your Listing
                      </Badge>
                    </div>
                  )}
                  {isHighlighted && !listing.is_own && (
                    <div className="flex items-center gap-1.5 mb-3">
                      <Badge className="bg-primary/10 text-primary text-[10px] font-semibold border-primary/20" data-testid="recommended-badge">
                        <Sparkles className="w-3 h-3 mr-1" />Recommended Match
                      </Badge>
                    </div>
                  )}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      {listing.owner.profile_photo ? (
                        <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-accent/20">
                          <img 
                            src={resolveImageUrl(listing.owner.profile_photo)} 
                            alt={listing.owner.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ) : (
                        <div className="w-12 h-12 bg-accent rounded-full flex items-center justify-center">
                          <span className="text-white font-bold text-lg">
                            {listing.owner.name.charAt(0)}
                          </span>
                        </div>
                      )}
                      <div>
                        <div className="flex items-center gap-1.5">
                          <Link to={`/card/${listing.user_id}`} className="font-semibold hover:text-primary transition-colors" data-testid={`listing-name-${listing.id}`}>{listing.owner.name}</Link>
                          {listing.owner.is_sample && (
                            <Badge variant="secondary" className="text-[10px] px-1.5 py-0 border border-muted-foreground/20 flex items-center gap-0.5" data-testid={`sample-badge-${listing.id}`}>
                              <FlaskConical className="w-2.5 h-2.5" />
                              Demo
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-accent">{listing.owner.profession}</p>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      {listing.available_slots > 0 ? (
                        <Badge variant="default" className="bg-accent text-white text-xs font-bold px-2 py-1">
                          {listing.available_slots} slot{listing.available_slots !== 1 ? 's' : ''}
                        </Badge>
                      ) : (
                        <Badge className="bg-red-100 text-red-700 text-[10px] font-semibold border-red-200">
                          All Full
                        </Badge>
                      )}
                      {!listing.is_own && listing.available_slots === 0 && (
                        <Badge className="bg-amber-100 text-amber-700 text-[10px] font-semibold border-amber-200 flex items-center gap-1">
                          <TrendingUp className="w-2.5 h-2.5" />
                          Bidding
                        </Badge>
                      )}
                      {listing.owner.is_trial && (
                        <Badge variant="secondary" className="text-[10px]">Trial User</Badge>
                      )}
                    </div>
                  </div>

                  {listing.owner.company && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                      <Briefcase className="w-4 h-4" />
                      {listing.owner.company}
                    </div>
                  )}

                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                    <MapPin className="w-4 h-4" />
                    {listing.owner.city}, {listing.owner.state}
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-border/50">
                    <div className="flex items-center gap-2">
                      <Star className="w-4 h-4 text-yellow-500" />
                      <span className="text-sm font-medium">
                        {listing.owner.reputation_score?.toFixed(1) || '0.0'} rep
                      </span>
                    </div>
                    <div className="flex items-center gap-1 text-sm font-semibold text-green-600">
                      <DollarSign className="w-4 h-4" />
                      20/mo per slot
                    </div>
                  </div>

                  {/* Filled Slots — Show Advertisers */}
                  {listing.filled_slots && listing.filled_slots.length > 0 && !listing.is_own && (
                    <div className="mt-3 mb-1">
                      <p className="text-xs text-muted-foreground font-medium mb-2">Currently advertising:</p>
                      <div className="space-y-1.5">
                        {listing.filled_slots.map((fs) => (
                          <div key={fs.position} className="flex items-center gap-2 px-2.5 py-1.5 rounded-md bg-blue-50 border border-blue-100" data-testid={`filled-slot-${listing.id}-${fs.position}`}>
                            <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                              <span className="text-white text-[10px] font-bold">{fs.advertiser.initial}</span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <span className="text-xs font-medium text-blue-800 truncate block">{fs.advertiser.name}</span>
                              <span className="text-[10px] text-blue-600 truncate block">{fs.advertiser.profession}{fs.advertiser.company ? ` · ${fs.advertiser.company}` : ''}</span>
                            </div>
                            <Badge variant="outline" className="text-[10px] border-blue-200 text-blue-600 shrink-0">Slot #{fs.position}</Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Slot Selection */}
                  <div className="mt-4 space-y-2">
                    {listing.owner.is_sample ? (
                      <p className="text-xs text-amber-600 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 rounded-md flex items-center gap-1.5">
                        <FlaskConical className="w-3.5 h-3.5 flex-shrink-0" />
                        Demo account — for illustration only. Ads cannot be placed here.
                      </p>
                    ) : listing.is_own ? (
                      <OwnSlotManager listing={listing} />
                    ) : listing.available_slots === 0 ? (
                      /* All slots full — waiting list + bidding */
                      <div className="space-y-3" data-testid={`full-slots-${listing.id}`}>
                        <div className="flex items-center gap-2 bg-red-50 dark:bg-red-950/20 border border-red-200 rounded-lg px-3 py-2">
                          <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                          <span className="text-xs font-semibold text-red-700">All {listing.total_slots || 5} slots are full — high demand!</span>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1 text-xs h-9 border-amber-300 text-amber-700 hover:bg-amber-50"
                            onClick={() => handleJoinWaitlist(listing.user_id)}
                            disabled={joiningWaitlist === listing.user_id}
                            data-testid={`join-waitlist-${listing.id}`}
                          >
                            {joiningWaitlist === listing.user_id ? (
                              'Joining...'
                            ) : (
                              <>
                                <Clock className="w-3.5 h-3.5 mr-1" />
                                Join Waiting List
                              </>
                            )}
                          </Button>
                          <Button
                            size="sm"
                            className="flex-1 text-xs h-9 bg-amber-500 hover:bg-amber-600 text-white"
                            onClick={() => openBidDialog(listing)}
                            data-testid={`place-bid-${listing.id}`}
                          >
                            <TrendingUp className="w-3.5 h-3.5 mr-1" />
                            Place Premium Bid
                          </Button>
                        </div>
                      </div>
                    ) : (
                      /* Available slots */
                      <div>
                        <p className="text-xs text-muted-foreground font-medium mb-2">Select a slot to place your ad:</p>
                      <div className="flex flex-wrap gap-2">
                        {listing.slots.map((slot) => {
                          const alreadyRequested = hasRequestedSlot(slot.slot_id);
                          const slotPrice = slot.price_per_month || 20;
                          return (
                            <Button
                              key={slot.slot_id}
                              size="sm"
                              variant={alreadyRequested ? "secondary" : "outline"}
                              disabled={alreadyRequested}
                              onClick={() => openAdDialog(listing, slot)}
                              className={`text-xs h-9 ${!alreadyRequested ? 'border-green-300 hover:bg-green-50 hover:border-green-400' : ''}`}
                              data-testid={`slot-btn-${slot.slot_id}`}
                            >
                              <BadgeDollarSign className={`w-3.5 h-3.5 mr-1 ${alreadyRequested ? 'text-muted-foreground' : 'text-green-500'}`} />
                              #{slot.position} — ${slotPrice}/mo
                              {alreadyRequested && <span className="ml-1 text-muted-foreground">(Pending)</span>}
                            </Button>
                          );
                        })}
                      </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
            })}
          </div>
        )
        )}

        {/* Add Additional Slot Dialog */}
        <Dialog open={addSlotDialogOpen} onOpenChange={(open) => { setAddSlotDialogOpen(open); if (!open) setAddSlotListing(null); }}>
          <DialogContent className="sm:max-w-sm" data-testid="add-slot-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Plus className="w-5 h-5 text-green-600" />
                Add a Marketing Slot
              </DialogTitle>
              <DialogDescription>
                Add another ad slot to your signature — attract more advertisers and earn more.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4 space-y-4">
              <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Monthly rate</span>
                  <span className="font-bold text-xl text-green-600">$20/mo</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Per additional slot</span>
                  <span className="font-medium">Monthly subscription</span>
                </div>
                <div className="border-t border-border pt-2">
                  <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                    <CreditCard className="w-3.5 h-3.5 flex-shrink-0" />
                    Pre-pay $20 to reserve this slot. Renews monthly. Cancel anytime.
                  </p>
                </div>
              </div>
              <div className="text-xs text-muted-foreground bg-green-50 dark:bg-green-900/10 rounded-md p-3">
                <strong>Current slots:</strong> {addSlotListing?.slots?.length || 0} of 5 maximum.
                Each slot can earn you <strong className="text-green-600">$10/month</strong> when filled (50% of $20).
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setAddSlotDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={handlePurchaseAdSlot}
                disabled={purchasingSlot}
                className="bg-green-600 hover:bg-green-700 text-white gap-2"
                data-testid="purchase-slot-btn"
              >
                <CreditCard className="w-4 h-4" />
                {purchasingSlot ? 'Redirecting…' : 'Pay $20/mo — Reserve Slot'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Place Ad Dialog */}
        <Dialog open={adDialogOpen} onOpenChange={(open) => {
          setAdDialogOpen(open);
          if (!open) {
            setSelectedListing(null);
            setSelectedSlot(null);
          }
        }}>
          <DialogContent data-testid="place-ad-dialog">
            <DialogHeader>
              <DialogTitle>Place Your Ad</DialogTitle>
              <DialogDescription>
                {selectedListing && selectedSlot && (
                  <>Advertise on {selectedListing.owner.name}'s email signature — Slot #{selectedSlot.position}</>
                )}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="p-4 bg-muted/50 rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Monthly Rate</span>
                  <span className="font-bold text-lg">$20.00/mo</span>
                </div>
                <div className="flex justify-between text-sm text-muted-foreground">
                  <span>Signature owner earns</span>
                  <span className="text-green-600 font-medium">$10.00 (50%)</span>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Message to signature owner (optional)</Label>
                <Textarea
                  placeholder="Introduce yourself and explain why your ad would be a good fit..."
                  value={adMessage}
                  onChange={(e) => setAdMessage(e.target.value)}
                  className="min-h-[100px]"
                  data-testid="ad-message-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAdDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handlePlaceAd} disabled={submitting} data-testid="confirm-ad-btn">
                {submitting ? 'Submitting...' : 'Submit Ad Request'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Founding Member — Extra Ad Slot Unlock Dialog */}
        <Dialog open={foundingExtraSlot.open} onOpenChange={(open) => setFoundingExtraSlot(s => ({ ...s, open }))}>
          <DialogContent className="sm:max-w-md w-[calc(100%-1.5rem)] max-h-[90vh] overflow-y-auto p-4 sm:p-6" data-testid="founding-extra-slot-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
                <span className="inline-flex w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-gradient-to-br from-amber-400 to-yellow-500 items-center justify-center text-white flex-shrink-0">
                  <Trophy className="w-4 h-4 sm:w-5 sm:h-5" />
                </span>
                Unlock another ad slot
              </DialogTitle>
              <DialogDescription className="pt-2 text-sm">
                As a <strong>Founding Member</strong> you get <strong>1 free ad slot</strong> for life.
                You're already advertising in <strong>{foundingExtraSlot.inUse}</strong> {foundingExtraSlot.inUse === 1 ? 'slot' : 'slots'}.
              </DialogDescription>
            </DialogHeader>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 sm:p-4 my-2">
              <p className="text-sm font-semibold text-amber-900">Unlock another slot — one-time ${foundingExtraSlot.amount}</p>
              <p className="text-xs text-amber-700 mt-1">
                Each additional concurrent ad slot is a one-time <strong>${foundingExtraSlot.amount} payment</strong>.
                It stays unlocked even if you cancel that placement later.
              </p>
            </div>

            <ul className="text-xs text-muted-foreground space-y-1.5">
              <li className="flex items-start gap-2"><Check className="w-3.5 h-3.5 text-emerald-500 mt-0.5 flex-shrink-0" /> Pay once — no monthly subscription</li>
              <li className="flex items-start gap-2"><Check className="w-3.5 h-3.5 text-emerald-500 mt-0.5 flex-shrink-0" /> Lifetime unlock for this extra slot</li>
              <li className="flex items-start gap-2"><Check className="w-3.5 h-3.5 text-emerald-500 mt-0.5 flex-shrink-0" /> Same 50% commission you always earn</li>
            </ul>

            <DialogFooter className="gap-2 pt-2 flex-col-reverse sm:flex-row">
              <Button
                variant="outline"
                onClick={() => setFoundingExtraSlot(s => ({ ...s, open: false }))}
                data-testid="founding-extra-slot-cancel"
                className="w-full sm:w-auto"
              >
                Not now
              </Button>
              <Button
                className="bg-amber-600 hover:bg-amber-700 text-white w-full sm:w-auto"
                onClick={handleUnlockExtraSlot}
                disabled={unlockingExtraSlot}
                data-testid="founding-extra-slot-pay"
              >
                {unlockingExtraSlot ? (
                  <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Redirecting…</>
                ) : (
                  <>Pay ${foundingExtraSlot.amount} · Unlock</>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* My Ad Requests */}
        {myAds.length > 0 && (
          <div className="mt-12">
            <h2 className="text-xl font-semibold font-['Outfit'] mb-4">Your Ad Requests</h2>
            <div className="space-y-3">
              {myAds.map((ad) => (
                <Card key={ad.id} className="border-border/50" data-testid={`my-ad-${ad.id}`}>
                  <CardContent className="p-4 flex items-center justify-between">
                    <div>
                      <p className="font-medium">
                        {ad.owner_name ? `${ad.owner_name} — ` : ''}Slot #{ad.position}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {ad.owner_profession ? `${ad.owner_profession} • ` : ''}$20/mo &middot; {new Date(ad.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={
                        ad.status === 'approved' ? 'default' :
                        ad.status === 'rejected' ? 'destructive' : 'secondary'
                      } data-testid={`bid-status-${ad.id}`}>
                        {ad.status}
                      </Badge>
                      {ad.status === 'pending' && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 border-red-200 hover:bg-red-50"
                          onClick={async () => {
                            if (!window.confirm('Withdraw this pending ad request?')) return;
                            try {
                              await api.cancelBid(ad.id);
                              setMyAds(prev => prev.filter(b => b.id !== ad.id));
                              toast.success('Ad request withdrawn');
                            } catch { toast.error('Failed to withdraw request'); }
                          }}
                          data-testid={`withdraw-bid-${ad.id}`}
                        >
                          <XCircle className="w-3.5 h-3.5 mr-1" />
                          Withdraw
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Premium Bid Dialog */}
        <Dialog open={bidDialogOpen} onOpenChange={(open) => { setBidDialogOpen(open); if (!open) setBidTarget(null); }}>
          <DialogContent data-testid="premium-bid-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-amber-500" />
                Place a Premium Bid
              </DialogTitle>
              <DialogDescription>
                {bidTarget && (
                  <span>Bid for an ad slot on <strong>{bidTarget.owner?.name}</strong>'s email signature. Higher bids get priority.</span>
                )}
              </DialogDescription>
            </DialogHeader>
            <div className="py-4 space-y-4">
              <div className="bg-amber-50 dark:bg-amber-950/20 rounded-lg p-4 space-y-3">
                <div>
                  <label className="text-sm font-medium mb-1.5 block">Your bid ($/month)</label>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" className="h-9 w-9 p-0" onClick={() => setBidAmount(Math.max(20, bidAmount - 5))} data-testid="bid-decrease">
                      <ChevronDown className="w-4 h-4" />
                    </Button>
                    <div className="flex items-center border rounded-md bg-background px-3 flex-1">
                      <DollarSign className="w-4 h-4 text-muted-foreground" />
                      <input
                        type="number"
                        value={bidAmount}
                        onChange={(e) => setBidAmount(Math.max(20, Number(e.target.value)))}
                        className="w-full py-2 text-lg font-bold bg-transparent focus:outline-none"
                        min={20}
                        data-testid="bid-amount-input"
                      />
                      <span className="text-sm text-muted-foreground">/mo</span>
                    </div>
                    <Button size="sm" variant="outline" className="h-9 w-9 p-0" onClick={() => setBidAmount(bidAmount + 5)} data-testid="bid-increase">
                      <ChevronUp className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div className="text-xs text-amber-700 flex items-center gap-1.5">
                  <Zap className="w-3.5 h-3.5" />
                  Minimum bid: $20/mo. The higher your bid, the better your chances.
                </div>
              </div>
              <div>
                <label className="text-sm font-medium mb-1.5 block">Message (optional)</label>
                <textarea
                  value={bidMessage}
                  onChange={(e) => setBidMessage(e.target.value)}
                  placeholder="Why you'd be a great ad partner..."
                  className="w-full border rounded-md p-3 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-amber-300 resize-none h-20"
                  data-testid="bid-message-input"
                />
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setBidDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={handlePlaceBid}
                disabled={submittingBid || bidAmount < 20}
                className="bg-amber-500 hover:bg-amber-600 text-white gap-2"
                data-testid="submit-bid-btn"
              >
                <TrendingUp className="w-4 h-4" />
                {submittingBid ? 'Submitting...' : `Bid $${bidAmount}/mo`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Marketplace Invite Popup - shown on each visit */}
        {showInvitePopup && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => { setShowInvitePopup(false); sessionStorage.setItem('cm_marketplace_invite_seen', 'true'); }}>
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in zoom-in-95 fade-in duration-200" onClick={e => e.stopPropagation()} data-testid="marketplace-invite-popup">
              <div className="bg-gradient-to-r from-emerald-600 to-green-500 p-6 text-center relative">
                <button onClick={() => { setShowInvitePopup(false); sessionStorage.setItem('cm_marketplace_invite_seen', 'true'); }}
                  className="absolute top-3 right-3 p-1.5 hover:bg-white/20 rounded-full transition-colors">
                  <X className="w-5 h-5 text-white" />
                </button>
                <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-3">
                  <Gift className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-xl font-bold text-white font-['Outfit']">Grow Your Revenue!</h2>
              </div>
              <div className="p-6 text-center space-y-4">
                <p className="text-sm text-gray-700 leading-relaxed">
                  Invite <strong className="text-emerald-700">2 pros</strong> to advertise on your email signature and get a <strong className="text-emerald-700">1 free ad slot</strong> to advertise on!
                </p>
                <div className="flex items-center justify-center gap-6 py-2">
                  <div className="text-center">
                    <div className="w-10 h-10 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-1">
                      <UserPlus className="w-5 h-5 text-emerald-600" />
                    </div>
                    <p className="text-xs text-gray-500">Invite 2 Pros</p>
                  </div>
                  <div className="text-emerald-400 font-bold">&rarr;</div>
                  <div className="text-center">
                    <div className="w-10 h-10 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-1">
                      <Megaphone className="w-5 h-5 text-amber-600" />
                    </div>
                    <p className="text-xs text-gray-500">They Advertise</p>
                  </div>
                  <div className="text-emerald-400 font-bold">&rarr;</div>
                  <div className="text-center">
                    <div className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-1">
                      <Star className="w-5 h-5 text-blue-600" />
                    </div>
                    <p className="text-xs text-gray-500">Free Slot!</p>
                  </div>
                </div>
                <Button className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold"
                  onClick={() => { setShowInvitePopup(false); sessionStorage.setItem('cm_marketplace_invite_seen', 'true'); setShowInviteModal(true); }}
                  data-testid="popup-invite-pro-btn">
                  <UserPlus className="w-4 h-4 mr-2" />Invite a Pro Now
                </Button>
                <button onClick={() => { setShowInvitePopup(false); sessionStorage.setItem('cm_marketplace_invite_seen', 'true'); }}
                  className="text-xs text-gray-400 hover:text-gray-600">
                  Maybe later
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Invite Pro Modal (reusable inline version) */}
        {showInviteModal && (
          <MarketplaceInviteModal onClose={() => setShowInviteModal(false)} />
        )}

        {/* Slot Activation Modal - removed (all features free) */}
      </div>
  );

  return embedded ? content : <Layout>{content}</Layout>;
};

export default AdMarketplace;
